import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Unlock } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   batchLabel?: string,
 *   purchasePrice?: number|string,
 *   consideredCost?: number|string,
 *   submitting?: boolean,
 *   submitError?: string,
 *   onSubmit?: (payload: { reason: string }) => void,
 * }} props
 */
export function RequestUnlockModal({
  open,
  onOpenChange,
  batchLabel = "",
  purchasePrice,
  consideredCost,
  submitting = false,
  submitError = "",
  onSubmit,
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason("");
    setError("");
  }, [open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError(t("validation.reasonRequired"));
      return;
    }
    if (reason.trim().length < 10) {
      setError(t("inventory.reasonMinLength"));
      return;
    }
    if (typeof onSubmit === "function") {
      onSubmit({ reason: reason.trim() });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-xs fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[480px] max-h-[85vh]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-4 font-sans focus:outline-hidden overflow-hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <Unlock className="h-4.5 w-4.5 text-sky-600" />
              <DialogTitle className="text-[16px] font-bold text-foreground">
                {t("formActions.requestPriceUnlock")}
              </DialogTitle>
            </div>
            <DialogPrimitive.Close
              disabled={submitting}
              className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition focus:outline-hidden disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          <p className="text-xs per-file-hint font-semibold leading-relaxed shrink-0">
            {t("inventory.unlockRequestHint")}
            {batchLabel ? (
              <>
                {" "}
                <strong className="text-foreground font-mono">{batchLabel}</strong>
              </>
            ) : null}
          </p>

          <div className="rounded-xl border border-(--color-text-border)/50 bg-workspace/20 px-3.5 py-2.5 text-xs space-y-1.5 shrink-0">
            <div className="flex justify-between gap-3">
              <span className="per-file-hint font-semibold">
                {t("inventory.pricePerUnit")}
              </span>
              <span className="font-mono font-bold">{purchasePrice ?? "—"}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="per-file-hint font-semibold">
                {t("inventory.consideredCostPerUnit")}
              </span>
              <span className="font-mono font-bold">{consideredCost ?? "—"}</span>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 min-h-0 flex-1 overflow-hidden"
          >
            <div className="flex flex-col gap-2 shrink-0">
              <label
                htmlFor="request-unlock-reason"
                className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400"
              >
                {t("inventory.reasonForUnlock")}{" "}
                <span className="text-rose-500">*</span>
              </label>
              <textarea
                id="request-unlock-reason"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (error) setError("");
                }}
                placeholder={t("formActions.unlockReasonPlaceholder")}
                disabled={submitting}
                rows={4}
                className={cn(
                  "w-full min-h-24 p-3 rounded-xl border bg-white text-xs font-semibold leading-relaxed",
                  "focus:outline-hidden focus:ring-2 focus:ring-accent/10 resize-y",
                  error || submitError
                    ? "border-rose-500 focus:border-rose-500"
                    : "border-(--color-text-border) focus:border-accent"
                )}
              />
            </div>

            {error || submitError ? (
              <p className="text-[11px] text-rose-500 font-bold shrink-0">
                ⚠️ {error || submitError}
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-(--color-text-border)/50 shrink-0">
              <button
                type="button"
                disabled={submitting}
                onClick={() => onOpenChange(false)}
                className="h-10 px-4 rounded-xl border border-(--color-text-border) per-file-hint font-bold text-xs hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
              >
                {t("formActions.cancel")}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="h-10 px-5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-bold text-xs transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? t("inventory.saving")
                  : t("formActions.submitRequest")}
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
